import React, { Fragment, useState } from 'react';
import { Box, Button, Divider, Grid, SelectChangeEvent, Tooltip, Typography } from '@mui/material';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectActiveSidebarById,
  selectRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { isApprovalTableElement } from 'utils/typeGuards';
import EditIcon from '@mui/icons-material/Edit';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { AzulCheckbox } from '@the-ksquare-group/azul-ds';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import { parseJustifyContentToAlignment } from 'utils/alignmentParsers';
import {
  ApprovalClickableVariables,
  getCellIdentifierByApprovalVariable,
} from 'pages/DocumentBuilder/components/ContentSection/components/ElementSection/components/ApprovalTable/ApprovalTable.utils';
import { Tag } from 'components/StyleCheckbox/StyleCheckbox';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { openInNewTab } from 'utils/openInNewTab';
import { selectContract } from 'features/salesforce/salesforceSlice';

interface ApprovalTableSidebarProps {
  currentElement: selectedElement;
}

export default function ApprovalSidebar({ currentElement }: ApprovalTableSidebarProps) {
  const tableElement = currentElement.data;

  if (!isApprovalTableElement(tableElement)) {
    return null;
  }

  const { pageId, rowId, columnId } = currentElement;
  const { content, recipients } = tableElement;
  const { approvalInfo } = content;
  const [tagAlignments, setTagAlignments] = useState({
    companySignedDate: {
      label: parseJustifyContentToAlignment(approvalInfo.companySignedDate.justifyContent),
      value: parseJustifyContentToAlignment(approvalInfo.companySignedDate.justifyContent),
    },
    companySignature: {
      label: parseJustifyContentToAlignment(approvalInfo.companySignature.justifyContent),
      value: parseJustifyContentToAlignment(approvalInfo.companySignature.justifyContent),
    },
    customerSignedDate: {
      label: parseJustifyContentToAlignment(approvalInfo.customerSignedDate.justifyContent),
      value: parseJustifyContentToAlignment(approvalInfo.customerSignedDate.justifyContent),
    },
    customerSignature: {
      label: parseJustifyContentToAlignment(approvalInfo.customerSignature.justifyContent),
      value: parseJustifyContentToAlignment(approvalInfo.customerSignature.justifyContent),
    },
  });

  const dispatch = useAppDispatch();

  const activeSidebar = useAppSelector(state =>
    selectActiveSidebarById(state, pageId, rowId, columnId, currentElement.data.id)
  );
  const recipientOptions = useAppSelector(selectRecipients);
  const salesforceBaseUrl = useAppSelector(state => state.auth.salesforceBaseUrl);
  const activeContract = useAppSelector(selectContract);

  const handleOnChangeRecipient = (type: ApprovalClickableVariables, event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));
    const selectedRecipient = recipientOptions.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));
      const { fullName: recipientName } = selectedRecipient;
      const cellIds = getCellIdentifierByApprovalVariable(type);

      const newContent = {
        ...content,
        activeSidebar: activeSidebar,
        table: {
          ...content.table,
          cells: {
            ...content.table.cells,
            [`cell-${cellIds.signature}`]: {
              ...content.table.cells[`cell-${cellIds.signature}`],
              content: `${recipientName} Signature`,
            },
            [`cell-${cellIds.date}`]: {
              ...content.table.cells[`cell-${cellIds.date}`],
              content: `${recipientName} Date`,
            },
          },
        },
      };

      dispatch(
        updateContentElementById({
          content: newContent,
          recipients: { ...recipients, [getRecipientType(type)]: selectedRecipient },
        })
      );
    }
  };

  const handleOnChangeRequired = (newRequired: boolean, type: ApprovalClickableVariables) => {
    const newContent = {
      ...content,
      activeSidebar: activeSidebar,
      approvalInfo: {
        ...content.approvalInfo,
        [type]: {
          ...content.approvalInfo[type],
          required: newRequired,
        },
      },
    };

    dispatch(
      updateContentElementById({
        content: newContent,
      })
    );
  };

  const handleOnChangeAlignment = (newAlignment: Tag, type: ApprovalClickableVariables) => {
    setTagAlignments(prev => ({ ...prev, [type]: newAlignment }));

    const newContent = {
      ...content,
      activeSidebar: activeSidebar,
      approvalInfo: {
        ...content.approvalInfo,
        [type]: {
          ...content.approvalInfo[type],
          justifyContent: parseJustifyContentToAlignment(newAlignment.value),
        },
      },
    };

    dispatch(
      updateContentElementById({
        content: newContent,
      })
    );
  };

  const getRecipientType = (type: ApprovalClickableVariables) => {
    if (type.includes('customer')) {
      return 'customerRecipient';
    } else {
      return 'companyRecipient';
    }
  };

  const handleClickEditContract = () => {
    if (salesforceBaseUrl && activeContract.Id) {
      openInNewTab(`${salesforceBaseUrl}/lightning/r/Contract/${activeContract.Id}/edit? `);
    }
  };

  const getCustomSidebar = (type: ApprovalClickableVariables) => {
    const title = type.includes('Date') ? 'Date' : 'Signature';
    return (
      <Fragment>
        <Typography fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
          {title} Block
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={'24px'} width='100%'>
          <Box alignItems={'center'} display={'flex'} gap='8px'>
            <Typography fontSize={'14px'} fontWeight={500}>
              Recipient
            </Typography>
            <Tooltip
              title='The selected recipient will be automatically set for both date and signature fields '
              arrow
              placement='top'
            >
              <InfoRoundedIcon color='primary' />
            </Tooltip>
          </Box>
          <RecipientSelect
            value={recipients[getRecipientType(type)]}
            recipientOptions={recipientOptions}
            editRecipients={false}
            onChange={event => handleOnChangeRecipient(type, event)}
          />
        </Box>
        <Divider
          color='#C9CACB'
          flexItem
          orientation='horizontal'
          variant='middle'
          sx={{ margin: '24px 0', width: '100%' }}
        />
        <AzulCheckbox
          checked={approvalInfo[type].required}
          label='Required'
          onChange={e => handleOnChangeRequired(e.target.checked, type)}
        />
        <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'} marginBottom={'24px'}>
          Alignment
        </Typography>
        <Box maxWidth={'210px'}>
          <AlignmentRadioGroup
            selected={tagAlignments[type]}
            handleChange={tag => handleOnChangeAlignment(tag, type)}
          />
        </Box>
      </Fragment>
    );
  };

  const getSidebar = (activeSidebar?: string) => {
    switch (activeSidebar) {
      case 'approvalTable':
        return (
          <Fragment>
            <Typography fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
              Approval Table Block
            </Typography>
            <Button
              variant='outlined'
              sx={{
                padding: '8px 12px',
                display: 'flex',
                gap: '8px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: '600',
              }}
              onClick={handleClickEditContract}
            >
              <EditIcon />
              Edit Contract In CRM
            </Button>
          </Fragment>
        );
      case 'customerSignedDate':
        return getCustomSidebar('customerSignedDate');
      case 'companySignedDate':
        return getCustomSidebar('companySignedDate');
      case 'customerSignature':
        return getCustomSidebar('customerSignature');
      case 'companySignature':
        return getCustomSidebar('companySignature');
      default:
        return;
    }
  };

  return (
    <Grid container direction='row' className='container' gap={2}>
      <Grid item xs={12}>
        {getSidebar(activeSidebar)}
      </Grid>
    </Grid>
  );
}
